import { takeLatest, takeEvery, call, put, select } from 'redux-saga/effects';
// redux-utils
import { genericSETypes as actionTypes } from 'redux-utils/types/index';
import { actionTypeFormatter } from 'redux-utils/sagas/index';
import { getFilterEpoch } from '../selectors/genericSESelector';
// api helpers
import { get, endpoints, post } from 'api/index';

// filter requests cascade: each response auto-selects an option, which fires
// the next request. a user picking a higher filter mid-cascade bumps the epoch,
// so every request already in flight is superseded and its response discarded.
const staleTypeByRequest = {
  [actionTypes.getFilter.request]: actionTypes.getFilter.stale,
  [actionTypes.getBottomUpFilter.request]: actionTypes.getBottomUpFilter.stale,
  // the three split session-data legs run in parallel, so they need the same
  // guard for a different reason: without it a leg computed under filter set A
  // can be assembled together with a leg from filter set B, producing a page
  // that renders happily and is silently wrong.
  [actionTypes.getSessionDataImages.request]:
    actionTypes.getSessionDataImages.stale,
  [actionTypes.getSessionDataMisc.request]:
    actionTypes.getSessionDataMisc.stale,
  [actionTypes.getSessionDataVariants.request]:
    actionTypes.getSessionDataVariants.stale
};

/**
 * @name appWatcherSaga
 * @description Watches for the action dispatched of certain type.
 */
const watcherSaga = [
  takeEvery(actionTypes.getChartData.request, getWorkerSaga),
  takeEvery(actionTypes.getChartList.request, getWorkerSaga),
  takeEvery(actionTypes.getFilters.request, getWorkerSaga),
  takeLatest(actionTypes.getSessionDataImages.request, getWorkerSaga),
  takeLatest(actionTypes.getSessionDataMisc.request, getWorkerSaga),
  takeLatest(actionTypes.getSessionDataVariants.request, getWorkerSaga),
  takeEvery(actionTypes.downloadChartData.request, getWorkerSaga),
  takeEvery(actionTypes.sessionFilterValues.request, getWorkerSaga),
  takeEvery(actionTypes.getFilter.request, getWorkerSaga),
  takeEvery(actionTypes.getBottomUpFilter.request, getWorkerSaga)
];

function getWorkerSaga(action) {
  switch (action.type) {
    case actionTypes.getChartData.request: {
      return apiWorkerSaga(action, {
        uriPath: endpoints.chartData,
        method: get,
        headers: ['auth']
      });
    }
    case actionTypes.getFilter.request: {
      const apiVersion = action.payload.params.api_version;
      return apiWorkerSaga(action, {
        uriPath:
          apiVersion === 'v4'
            ? endpoints.storeExplorer.v4filter
            : endpoints.storeExplorer.filter,
        method: post,
        headers: ['auth']
      });
    }
    case actionTypes.getBottomUpFilter.request: {
      const filterType = action.payload.params.filter_type;
      const apiVersion = action.payload.params.api_version;
      console.log('the filter type is', filterType, apiVersion);
      const shouldCallOldApi = apiVersion === 'v2';

      return apiWorkerSaga(action, {
        uriPath: shouldCallOldApi
          ? endpoints.storeExplorer.filter
          : endpoints.storeExplorer.v4filter,
        method: post,
        headers: ['auth']
      });
    }
    case actionTypes.downloadChartData.request: {
      return apiWorkerSaga(action, {
        uriPath: process.env.REACT_APP_SHELF_GENERATE_REPORT,
        method: get,
        headers: ['auth']
      });
    }
    case actionTypes.getChartList.request: {
      return apiWorkerSaga(action, {
        uriPath: endpoints.chartList,
        method: get,
        headers: ['auth']
      });
    }
    case actionTypes.getFilters.request: {
      return apiWorkerSaga(action, {
        uriPath: endpoints.storeExplorer.filters,
        method: get,
        headers: ['auth']
      });
    }
    case actionTypes.getSessionDataImages.request: {
      return apiWorkerSaga(action, {
        uriPath: endpoints.storeExplorer.sessionDataImages,
        method: get,
        headers: ['auth']
      });
    }
    case actionTypes.getSessionDataMisc.request: {
      return apiWorkerSaga(action, {
        uriPath: endpoints.storeExplorer.sessionDataMisc,
        method: get,
        headers: ['auth']
      });
    }
    case actionTypes.getSessionDataVariants.request: {
      return apiWorkerSaga(action, {
        uriPath: endpoints.storeExplorer.sessionDataVariants,
        method: get,
        headers: ['auth']
      });
    }
    case actionTypes.sessionFilterValues.request: {
      return apiWorkerSaga(action, {
        uriPath: endpoints.storeExplorer.sessionFilterValues,
        method: get,
        headers: ['auth']
      });
    }
    default:
      return null;
  }
}

/**
 * @description Get layout worker saga
 * @param {object} action - dispatched action
 * @param {object} params - api params
 * @returns {undefined}
 * @yields put - get layout success/error action.
 */
function* apiWorkerSaga(action, params) {
  const staleType = staleTypeByRequest[action.type];
  let epochAtRequest = null;
  if (staleType) {
    epochAtRequest = yield select(getFilterEpoch);
  }
  try {
    const { response, error, request } = yield call(params.method, {
      payload: action.payload.params,
      useCachedInstance: false,
      ...params
    });
    if (staleType && (yield select(getFilterEpoch)) !== epochAtRequest) {
      // the selection this response was computed under is gone. writing
      // nothing also stops the cascade: no state change, no re-render, so
      // GenericSEFilters never auto-selects and never fires the next request.
      yield put({
        meta: action.meta,
        type: staleType,
        payload: {
          ...action.payload,
          error: null,
          request,
          response: null
        }
      });
      return;
    }
    if (response) {
      yield put({
        meta: action.meta,
        type: actionTypeFormatter(action.type, 'SUCCESS'),
        payload: {
          ...action.payload,
          error: null,
          request,
          response: response.data
        }
      });
    } else {
      yield put({
        meta: action.meta,
        type: actionTypeFormatter(action.type, 'FAILURE'),
        payload: {
          ...action.payload,
          error: error.data,
          request,
          response: null
        }
      });
    }
  } catch (error) {
    yield put({
      meta: action.meta,
      type: 'SAGA_ERROR',
      payload: {
        type: action.type,
        ...action.payload,
        error
      }
    });
  }
}

export default watcherSaga;
